import React, { useEffect, useState } from 'react';
import TransactionWrapper from '../../../components/Transaction/TransactionWrapper';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import { useGetTransaction, usePutTransaction, usePostTransaction, useDeleteTransaction } from '../hooks/api/transactionApi';
import { useGetCustomerEnum, useGetContractsEnum, useGetProductEnum } from '../../../hooks/enums/useEnumHook';
import { useGetContract } from '../../ContractDetailPage/hooks/api/contractDetailApi';
import { formattedNumber } from '../../../utils/number';
import { useIntl } from 'react-intl';
import useAppContext from '../../../hooks/context/useAppContext';
import { API_DATE_TIME_FORMAT_FULL } from '../../../constants/date';

const skelet = (type) => {
  const count = type === 'quantities' ? 12 : 6;
  const skelet = [];
  for (let i = 1; i <= count; i++) {
    switch (type) {
      case 'quantities':
        skelet.push({ month: i, quantity: 0 });
        break;
      case 'tariff':
        skelet.push({ idTariff: i, value: 0, value2: 0 });
        break;
      default:
        skelet.push({});
    }
  }
  return skelet;
};

const TransactionDetail = ({ id }) => {
  const { getTransaction, data, loading, error } = useGetTransaction();
  const { putTransaction, data: putData, loading: putLoading, error: putError } = usePutTransaction();
  const { postTransaction, data: postData, loading: postLoading, error: postError } = usePostTransaction();
  const { getCustomerEnum, data: customerData, loading: customerEnumLoading } = useGetCustomerEnum();
  const { getContractsEnum, data: contractsData, loading: contractsEnumLoading } = useGetContractsEnum();
  const { getContract, data: contractData, loading: contractLoading } = useGetContract();
  const { getProductsEnum, data: productData, loading: productEnumLoading } = useGetProductEnum();
  const { deleteTransaction, data: deleteData, loading: deleteLoading, error: deleteError } = useDeleteTransaction();

  const [customersEnum, setCustomersEnum] = useState([]);
  const [contractsEnum, setContractsEnum] = useState([]);
  const [exchangeProductEnum, setExchangeProductEnum] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [contractId, setContractId] = useState('');
  const [transactionTypeId, setTransactionTypeId] = useState('');
  const [productId, setProductId] = useState('');
  const [purchaseTypesId, setPurchaseTypesId] = useState('');
  const [priceSourceId, setPriceSourceId] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [rate, setRate] = useState(0);
  const [price, setPrice] = useState(0);
  const [period, setPeriod] = useState('');
  const [createdAt, setCreatedAt] = useState(new Date());
  const [indexValues, setIndexValues] = useState(skelet('tariff'));
  const [tariffPrices, setTariffPrices] = useState(skelet('tariff'));
  const [quantities, setQuantities] = useState(skelet('quantities'));
  const [quantity, setQuantity] = useState(0);
  const [purchaseIndexTypeId, setPurchaseIndexTypeId] = useState('');

  const [state, setState] = useState(0);
  const [isNew, setIsNew] = useState(null);
  const [user, setUser] = useState(null);
  const [contract, setContract] = useState(null);
  const [commodity, setCommodity] = useState('');
  const intl = useIntl();
  const { setBreadCrumbs } = useAppContext();

  const transactionTitle = intl.formatMessage({ id: 'app.transaction.detail' });
  const transactionNewTitle = intl.formatMessage({ id: 'app.transaction.detailNew' });

  const settleState = [
    {
      id: 1,
      name: useIntl().formatMessage({ id: 'app.yes' }),
    },
    {
      id: 0,
      name: useIntl().formatMessage({ id: 'app.no' }),
    },
  ];

  useEffect(() => {
    if (id === 'new') {
      setBreadCrumbs(`${transactionNewTitle}`);
    } else {
      setBreadCrumbs(`${transactionTitle}`);
    }
  }, [setBreadCrumbs, transactionTitle, transactionNewTitle, id]);

  const sendHandler = () => {
    const body = {
      contract: { id: contractId },
      createdAt: format(new Date(createdAt), API_DATE_TIME_FORMAT_FULL),
      user: { id: user?.id },
      exchangeProduct: { id: productId },
      priceSource: { id: priceSourceId },
      purchaseIndexType: { id: purchaseIndexTypeId },
      purchaseType: { id: purchaseTypesId },
      currency: { id: currencyId },
      state: Number(state),
      isSale: false,
      quantity: formattedNumber(quantity, 3),
      quantityPercent: 1.0,
      price: formattedNumber(price, 3),
      rate: formattedNumber(rate, 3),
      sendNotification: false,
      quantities: quantities
        ?.filter((index) => formattedNumber(index.quantity) !== 0)
        .map((item) => {
          return { month: item.month, quantity: formattedNumber(item.quantity, 3) };
        }),

      indexValues: indexValues
        ?.filter((index) => formattedNumber(index.value) !== 0)
        .map((item) => {
          return {
            idTariff: item.idTariff,
            value: formattedNumber(item.value, 3),
            value2: formattedNumber(item.value2, 3),
          };
        }),

      tariffPrices: tariffPrices
        ?.filter((index) => formattedNumber(index.value) > 0)
        .map((item) => {
          return { idTariff: item.idTariff, value: formattedNumber(item.value, 3) };
        }),
      transactionType: { id: transactionTypeId },
    };
    if (id !== 'new') {
      Object.assign(body, { id: id });
      putTransaction(id, body);
    } else {
      postTransaction(body);
    }
  };

  const removeTransaction = () => {
    deleteTransaction(id);
  };

  const transaction = {
    id,
    state,
    user,
    customersEnum,
    settleState,
    customerId,
    contractId,
    contractsEnum,
    commodity,
    contract,
    exchangeProductEnum,
    productId,
    purchaseTypesId,
    priceSourceId,
    currencyId,
    transactionTypeId,
    rate,
    price,
    period,
    createdAt,
    indexValues,
    tariffPrices,
    quantities,
    quantity,
    purchaseIndexTypeId,
    putData,
    putError,
    putLoading,
    postData,
    postError,
    postLoading,
    isNew,
    deleteData,
    deleteLoading,
    deleteError,
    customerEnumLoading,
    productEnumLoading,
    contractLoading,
    contractsEnumLoading,
  };

  const handlers = {
    setState,
    setUser,
    setCustomerId,
    setContractId,
    setCreatedAt,
    setProductId,
    setPurchaseTypesId,
    setPriceSourceId,
    setRate,
    setPrice,
    setIndexValues,
    setTariffPrices,
    setTransactionTypeId,
    setQuantities,
    setQuantity,
    setPurchaseIndexTypeId,
    sendHandler,
    setIsNew,
    removeTransaction,
  };

  useEffect(() => {
    if (contract?.commodity?.id && contract?.contractPeriod) {
      (async () => {
        await getProductsEnum(contract.contractPeriod, contract.commodity.id);
      })();
    }
  }, [getProductsEnum, contract]);

  useEffect(() => {
    productData && setExchangeProductEnum(productData);
    contract && setCommodity(contract.commodity.name);
    contractData && setContract(contractData);
    contractData && setCurrencyId(contractData.currency.id);
    contractData && setPeriod(contractData.contractPeriod);
    customerData && setCustomersEnum(customerData);
    contractsData && setContractsEnum(contractsData);
  }, [setExchangeProductEnum, productData, setCommodity, contract, contractData, customerData, contractsData]);

  useEffect(() => {
    if (id !== 'new') {
      (async () => {
        await getTransaction(id);
      })();
    }
  }, [id, getTransaction]);

  useEffect(() => {
    if (data) {
      setContractId(data.contract.id);
      setState(data.state);
      setUser(data.user);
      setProductId(data.exchangeProduct.id);
      setCustomerId(data?.contract?.customer?.id);
      setPurchaseTypesId(data.purchaseType.id);
      setPriceSourceId(data.priceSource.id);
      setTransactionTypeId(data.transactionType.id);
      setRate(data.rate);
      setPrice(data.price);
      setCreatedAt(data.createdAt);
      setIndexValues(data.indexValues);
      setTariffPrices(data.tariffPrices);
      setQuantities(data.quantities);
      setQuantity(data.quantity);
      setPurchaseIndexTypeId(data.purchaseIndexType.id);
    }
    if (id !== 'new') {
      setIsNew(false);
    } else {
      setIsNew(true);
    }
  }, [data, loading, error, id]);

  useEffect(() => {
    if (contractId !== '') {
      (async () => {
        await getContract(contractId);
      })();
    }
  }, [contractId, getContract]);

  useEffect(() => {
    (async () => {
      await getCustomerEnum();
    })();
  }, [getCustomerEnum]);

  useEffect(() => {
    if (customerId) {
      (async () => {
        await getContractsEnum(customerId);
      })();
    }
  }, [customerId, getContractsEnum]);

  return (
    <>
      <TransactionWrapper transaction={transaction} loading={loading} error={error} handlers={handlers} isNew={isNew} id={id} />
    </>
  );
};

TransactionDetail.propTypes = {
  id: PropTypes.any,
};

export default TransactionDetail;
